// PWA/platform bits: screen wake lock, tab-visibility handling, iOS/Safari/
// standalone detection, and the one-time "add to home screen" hint.
import type { Dom } from './session.ts';
import type { Session } from './session.ts';

// ---------- wake lock ----------
export async function requestWakeLock(session: Session): Promise<void> {
  try {
    const nav = navigator as Navigator & { wakeLock?: { request(type: 'screen'): Promise<{ release(): Promise<void> }> } };
    if (nav.wakeLock) session.wakeLock = await nav.wakeLock.request('screen');
  } catch { /* unsupported or denied — a still screen just dims sooner */ }
}

export function releaseWakeLock(session: Session): void {
  if (session.wakeLock) { void session.wakeLock.release().catch(() => {}); session.wakeLock = null; }
}

export function onVisibilityChange(session: Session): void {
  session.sound.onVisibility(document.hidden);
  if (document.hidden) {
    releaseWakeLock(session);
  } else {
    session.last = performance.now(); // resume without a time jump — no simulated catch-up
    if (session.appState === 'playing') void requestWakeLock(session);
  }
}

// ---------- iOS / standalone / Safari detection ----------
export function isIOS(): boolean {
  const nav = navigator as Navigator & { maxTouchPoints?: number };
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (nav.platform === 'MacIntel' && (nav.maxTouchPoints ?? 0) > 1);
}

export function isStandalone(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true || matchMedia('(display-mode: standalone)').matches;
}

export function isSafariBrowser(): boolean {
  const ua = navigator.userAgent;
  return /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|Chrome|Android/.test(ua);
}

// ---------- install hint (iOS Safari, not standalone, shown once) ----------
const INSTALL_HINT_KEY = 'qs.installHintShown';

export function hideInstallHint(dom: Dom): void {
  dom.installHint.classList.add('hidden');
}

export function maybeShowInstallHint(dom: Dom): void {
  let alreadyShown = true;
  try { alreadyShown = !!localStorage.getItem(INSTALL_HINT_KEY); } catch { alreadyShown = true; }
  if (alreadyShown || !isIOS() || isStandalone() || !isSafariBrowser()) return;
  dom.installHint.classList.remove('hidden');
  try { localStorage.setItem(INSTALL_HINT_KEY, '1'); } catch { /* private mode — it'll just show again next time */ }
}
